import React, { useEffect, useMemo, useState } from 'react';
import {
  FaChevronRight,
  FaDiscord,
  FaExternalLinkAlt,
  FaHistory,
  FaImage,
  FaKey,
  FaPen,
  FaSignOutAlt,
  FaTrashAlt,
  FaUnlink,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BtnSimple } from '../components/form/btn.Simple';
import { InputSimple } from '../components/form/input.Simple';
import {
  WidgetSettingsRoot,
  WidgetSettingsSection,
} from '../components/form/settings.Widget';
import { ModalGlobal } from '../components/modal/modal.Global';
import { MarkdownEditModal } from '../components/text/text.EditModal';
import { TextMd } from '../components/text/text.Md';
import { ThemeChange } from '../components/theme/theme.Change';
import {
  deleteUserAccount,
  getAccountDeletionPreview,
  getDiscordAuthUrl,
  getMe,
  login,
  register,
  requestPasswordReset,
  unlinkDiscordAccount,
  updateProfile,
} from '../services/auth';
import { uploadPic, uploadPicFromUrl } from '../services/pic';
import { getStoredAuthUser } from '../utils/auth';
import { loadUserRoles, syncAuthSession } from '../utils/accountProfile';
import { renderIconBySlug } from '../content/icons';
import { logoSVG } from '../content/logo.svg';
import { setOpenModales } from '../store/global';

const roleLabels = {
  owner: 'Propriétaire',
  admin: 'Admin',
  editor: 'Éditeur',
  reader: 'Lecteur',
};

const discordStatusLabels = {
  connected: 'Connexion Discord réussie.',
  linked: 'Compte Discord lié.',
  already_linked: 'Ce compte Discord est déjà lié à un autre compte.',
  email_used:
    'Un compte existe déjà avec cet email. Connecte-toi puis lie Discord depuis ton profil.',
  user_not_found: 'Compte utilisateur introuvable.',
  error: 'Connexion Discord impossible.',
};

const discordErrorLabels = {
  password_required_before_unlink:
    'Définis un mot de passe avant de délier Discord, sinon ce compte ne pourra plus se connecter.',
};

const accountDeleteErrorLabels = {
  account_owns_items:
    'Impossible de supprimer ce compte tant qu’il possède encore des univers, dossiers ou items. Transfère ou supprime ces contenus avant de supprimer le compte.',
  missing_universe_choices:
    'Choisis quoi faire pour chaque univers qui possède au moins un admin.',
  invalid_universe_owner:
    'Le nouvel owner choisi doit être admin de cet univers.',
};

const profileSaveErrorLabels = {
  invalid_username: 'Le pseudo ne peut pas être vide.',
  username_already_used: 'Ce pseudo est déjà utilisé.',
};

const getRecentItemIdsFromStorage = () => {
  try {
    const saved = JSON.parse(
      window.localStorage.getItem('recentItemIds') || '[]',
    );
    return Array.isArray(saved) ? saved : [];
  } catch (e) {
    return [];
  }
};

const MessageLine = ({ message, tone = 'info' }) => {
  if (!message) return null;
  const toneClass =
    tone === 'error' ? 'text-secondary' : 'text-contrast text-opacity-75';
  return <div className={`text-sm ${toneClass}`}>{message}</div>;
};

const getThemeBackgroundStyle = color => {
  if (!color) return {};
  if (color === 'transparent') return { backgroundColor: 'transparent' };
  return { backgroundColor: `rgb(var(--${color}))` };
};

const getEntryAvatarAsset = item => {
  const picObj = Array.isArray(item?.pics) ? item.pics[0] : null;
  const picId = picObj ? Object.keys(picObj)[0] : null;
  const pic = picId ? picObj?.[picId] : null;
  return {
    url: pic?.url || null,
    iconSlug: pic?.iconSlug || null,
    bgColor: pic?.bgColor || null,
  };
};

const ProfileItemAvatar = ({ item, label }) => {
  const asset = getEntryAvatarAsset(item);
  const hasIcon = Boolean(asset.iconSlug);
  const hasImage = Boolean(asset.url) && !hasIcon;
  const bgStyle = getThemeBackgroundStyle(asset.bgColor);
  const icon = hasIcon
    ? renderIconBySlug(asset.iconSlug, {
        size: 24,
        color: 'var(--contrast)',
      })
    : null;

  return (
    <div
      className='flex h-3 w-3 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary bg-opacity-60 font-condensed text-sm uppercase text-contrast'
      style={{
        ...bgStyle,
        ...(hasImage
          ? {
              backgroundImage: `url(${asset.url})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
            }
          : {}),
      }}
    >
      {icon || (!hasImage ? label?.[0]?.toUpperCase() || '?' : null)}
    </div>
  );
};

const ProfileAvatarPreview = ({ authUser, avatarUrl, onClick }) => {
  const fallback = authUser?.username?.[0]?.toUpperCase() || '?';
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      aria-label={onClick ? "Modifier l'image de profil" : undefined}
      className='group relative flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary bg-opacity-20 font-condensed text-3xl font-bold uppercase text-secondary'
      onClick={onClick}
      style={
        avatarUrl
          ? {
              backgroundImage: `url(${avatarUrl})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    >
      {!avatarUrl && fallback}
      {onClick ? (
        <span className='absolute inset-0 flex items-center justify-center bg-main bg-opacity-45 text-contrast opacity-0 transition group-hover:opacity-100'>
          <FaPen size={16} />
        </span>
      ) : null}
    </Tag>
  );
};

const ProfileDescriptionDisplay = ({ descriptionMarkdown, onOpen }) => (
  <button
    type='button'
    className='group relative w-full p-0 pr-2 text-left transition hover:opacity-80'
    onDoubleClick={onOpen}
    title='Double-clic pour modifier'
  >
    <span className='absolute right-0 top-0 text-contrast text-opacity-45 opacity-70 transition group-hover:text-secondary group-hover:opacity-100'>
      <FaPen size={12} />
    </span>
    {descriptionMarkdown ? (
      <TextMd str={descriptionMarkdown} />
    ) : (
      <div className='text-sm text-contrast text-opacity-60'>
        Double-clic pour ajouter une description markdown.
      </div>
    )}
  </button>
);

const DiscordStatus = ({ authUser }) => {
  if (authUser.discordId) {
    const label =
      authUser.passwordLoginEnabled === true
        ? 'Discord lié comme méthode secondaire'
        : 'Compte connecté avec Discord';
    return (
      <div className='rounded bg-primary bg-opacity-20 px-075 py-03 text-sm text-contrast text-opacity-75'>
        {label}
        {authUser.discordUsername ? ` : ${authUser.discordUsername}` : ''}
      </div>
    );
  }

  return (
    <div className='rounded bg-main bg-opacity-35 px-075 py-03 text-sm text-contrast text-opacity-60'>
      Discord non lié
    </div>
  );
};

const ProfileImageModal = ({
  authUser,
  profileDraft,
  imageSize,
  profileImageLoading,
  profileSaving,
  profileMessage,
  profileError,
  onProfileDraftFieldChange,
  onProfileImageFromDevice,
  onProfileImageFromUrl,
  onClose,
}) => {
  const hasImage = Boolean(profileDraft.avatarUrl);
  const dimensionsLabel =
    hasImage &&
    Number.isFinite(imageSize.width) &&
    Number.isFinite(imageSize.height)
      ? `${imageSize.width} × ${imageSize.height}px`
      : null;

  return (
    <ModalGlobal
      title='edition'
      idToOpen='profileImage'
      width={960}
      maxWidthClass='max-w-6xl'
      onClose={onClose}
    >
      <WidgetSettingsRoot>
        <WidgetSettingsSection title='Image'>
          <div className='flex flex-col gap-1 lg:flex-row'>
            <div className='min-w-0 flex-2'>
              <div
                className='relative flex items-center justify-center overflow-hidden rounded bg-main bg-opacity-40'
                style={{ minHeight: 360, maxHeight: 'calc(100svh - 230px)' }}
              >
                {hasImage ? (
                  <img
                    src={profileDraft.avatarUrl}
                    alt='Prévisualisation'
                    className='block max-w-full object-contain'
                    style={{ maxHeight: 'calc(100svh - 230px)' }}
                  />
                ) : (
                  <div className='flex min-h-360 w-full flex-col items-center justify-center gap-05 text-contrast text-opacity-50'>
                    <FaImage className='text-4xl' />
                    <span className='text-sm'>Aucune image sélectionnée</span>
                  </div>
                )}
              </div>
              <div className='mt-05 flex flex-wrap items-center justify-between gap-05 text-xs text-contrast text-opacity-60'>
                <span>{dimensionsLabel || 'Aucune dimension détectée'}</span>
                {hasImage && (
                  <a
                    href={profileDraft.avatarUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='inline-flex items-center gap-03 text-primary hover:text-secondary'
                  >
                    <FaExternalLinkAlt />
                    Ouvrir l’image originale
                  </a>
                )}
              </div>
            </div>

            <div className='flex min-w-240 flex-1 flex-col gap-1'>
              <label className='relative flex w-full cursor-pointer items-center justify-center gap-1 rounded bg-primary px-1 py-03 shadow'>
                <FaImage />
                {profileImageLoading
                  ? 'Import en cours...'
                  : "Depuis l'appareil"}
                <input
                  className='invisible absolute'
                  type='file'
                  name='file'
                  onChange={onProfileImageFromDevice}
                  accept='image/*'
                  disabled={profileImageLoading}
                />
              </label>

              <div className='flex w-full'>
                <InputSimple
                  placeholder='depuis une url'
                  forcedValue={profileDraft.avatarUrl}
                  onChange={event =>
                    onProfileDraftFieldChange('avatarUrl', event.target.value)
                  }
                  onBlur={onProfileImageFromUrl}
                />
              </div>

              <div className='flex items-center gap-1 rounded bg-main bg-opacity-30 p-1'>
                <ProfileAvatarPreview
                  authUser={authUser}
                  avatarUrl={profileDraft.avatarUrl}
                />
                <div className='text-sm text-contrast text-opacity-70'>
                  Aperçu dans le profil
                </div>
              </div>

              {hasImage && (
                <button
                  type='button'
                  className='text-left text-xs text-secondary underline hover:text-primary'
                  onClick={() => onProfileDraftFieldChange('avatarUrl', '')}
                >
                  Supprimer l'image actuelle
                </button>
              )}
            </div>
          </div>
        </WidgetSettingsSection>
      </WidgetSettingsRoot>

      <MessageLine message={profileMessage} />
      <MessageLine
        message={profileError}
        tone='error'
      />
    </ModalGlobal>
  );
};

const ProfileDescriptionModal = ({
  profileDraft,
  profileSaving,
  profileMessage,
  profileError,
  onProfileDraftFieldChange,
  onClose,
}) => (
  <MarkdownEditModal
    idToOpen='profileDescription'
    title='Edition de la description'
    value={profileDraft.descriptionMarkdown}
    saving={profileSaving}
    message={profileMessage}
    error={profileError}
    onChange={value => onProfileDraftFieldChange('descriptionMarkdown', value)}
    onClose={onClose}
    hideSave
  />
);

const RoleCard = ({ entry, type, onOpen }) => (
  <button
    type='button'
    className='flex w-full items-center gap-1 rounded-lg bg-main bg-opacity-35 p-1 text-left transition hover:bg-secondary hover:bg-opacity-10'
    onClick={() => onOpen(entry._id)}
  >
    <ProfileItemAvatar
      item={entry.item || { label: entry.label }}
      label={entry.label}
    />
    <div className='min-w-0 flex-1'>
      <div className='truncate font-condensed text-lg font-bold'>
        {entry.label}
      </div>
      <div className='text-xs uppercase tracking-wide text-contrast text-opacity-55'>
        {roleLabels[entry.role] || entry.role}
      </div>
    </div>
    <FaChevronRight
      size={12}
      className='shrink-0 text-contrast text-opacity-60'
    />
  </button>
);

const RoleSection = ({ title, entries, type, loading, onOpen }) => (
  <section className='flex flex-1 flex-col gap-1 p-0 sm:rounded-lg sm:bg-main sm:bg-opacity-30 sm:p-1 sm:shadow'>
    <div className='flex items-center justify-between gap-1'>
      <h2 className='font-condensed text-xl font-bold uppercase'>{title}</h2>
      <span className='rounded bg-main bg-opacity-50 px-05 py-02 text-xs text-contrast text-opacity-60'>
        {entries.length}
      </span>
    </div>
    {loading ? (
      <div className='p-0 text-sm text-contrast text-opacity-60 sm:rounded sm:bg-main sm:bg-opacity-30 sm:p-1'>
        Chargement des accès...
      </div>
    ) : entries.length === 0 ? (
      <div className='p-0 text-sm text-contrast text-opacity-60 sm:rounded sm:bg-main sm:bg-opacity-30 sm:p-1'>
        Aucun accès
      </div>
    ) : (
      <div className='flex flex-col gap-05'>
        {entries.map(entry => (
          <RoleCard
            key={entry._id}
            entry={entry}
            type={type}
            onOpen={onOpen}
          />
        ))}
      </div>
    )}
  </section>
);

const ProfileHistorySidebar = ({
  navigationSize,
  recentItems,
  onOpenItem,
  onOpenPasswords,
}) => (
  <aside
    className='flex h-full min-h-0 flex-col overflow-hidden border-l border-main-d border-opacity-20 bg-main'
    style={{
      width: navigationSize || 200,
      minWidth: navigationSize || 200,
    }}
  >
    <div className='sticky top-0 flex items-center gap-1 bg-main bg-opacity-50 px-05 font-condensed text-lg font-bold backdrop-blur-md'>
      <FaHistory />
      Historique
    </div>
    <div className='flex-1 overflow-auto'>
      {recentItems.length > 0 ? (
        recentItems.map(item => (
          <button
            key={item?._id}
            type='button'
            className='flex w-full items-center gap-05 py-1 pl-1 pr-05 text-left transition hover:bg-main-d hover:opacity-50'
            onClick={() => onOpenItem(item?._id)}
          >
            <ProfileItemAvatar
              item={item}
              label={item?.label}
            />
            <span className='min-w-0 flex-1 truncate'>
              {item?.label || 'Item'}
            </span>
            <FaChevronRight className='shrink-0 text-xs text-contrast text-opacity-55' />
          </button>
        ))
      ) : (
        <div className='p-1 text-xs text-contrast text-opacity-60'>
          Aucun historique.
        </div>
      )}
    </div>
    <div className='sticky bottom-0 z-10 flex w-full justify-evenly gap-1 border-t border-main-d bg-main py-05'>
      <ThemeChange />
      <button
        type='button'
        className='cursor-pointer transition hover:opacity-50'
        title='Gérer les mots de passe'
        onClick={onOpenPasswords}
      >
        <FaKey />
      </button>
    </div>
  </aside>
);

const AuthPanel = ({
  mode,
  form,
  loading,
  message,
  error,
  onModeChange,
  onFieldChange,
  onSubmit,
  onDiscordLogin,
}) => (
  <section className='flex flex-col gap-1 p-0 sm:rounded-lg sm:bg-main sm:bg-opacity-40 sm:p-1 sm:shadow sm:backdrop-blur-sm lg:w-1/2'>
    <div>
      <div className='text-xs font-bold uppercase tracking-wide text-secondary'>
        Accès au compte
      </div>
      <h2 className='font-condensed text-3xl font-bold uppercase'>
        {mode === 'forgot'
          ? 'Mot de passe oublié'
          : mode === 'login'
            ? 'Connexion'
            : 'Créer un compte'}
      </h2>
      <p className='text-sm text-contrast text-opacity-65'>
        {mode === 'forgot'
          ? 'Saisis ton email pour recevoir un lien de réinitialisation.'
          : 'Connecte-toi pour retrouver tes préférences, favoris, templates et accès'}
      </p>
    </div>

    <div className='flex gap-05'>
      <BtnSimple
        sm
        color={mode === 'login' ? 'secondary' : null}
        border={mode === 'login' ? 'secondary' : null}
        onClick={() => onModeChange('login')}
      >
        Connexion
      </BtnSimple>
      <BtnSimple
        sm
        color={mode === 'register' ? 'secondary' : null}
        border={mode === 'register' ? 'secondary' : null}
        onClick={() => onModeChange('register')}
      >
        Inscription
      </BtnSimple>
      <BtnSimple
        sm
        color={mode === 'forgot' ? 'secondary' : null}
        border={mode === 'forgot' ? 'secondary' : null}
        onClick={() => onModeChange('forgot')}
      >
        Mot de passe oublié
      </BtnSimple>
    </div>

    {mode !== 'forgot' && (
      <BtnSimple
        className='flex items-center justify-center gap-05'
        disabled={loading}
        onClick={onDiscordLogin}
      >
        <FaDiscord />
        Continuer avec Discord
      </BtnSimple>
    )}

    <div className='flex flex-col gap-1 rounded-lg bg-primary bg-opacity-25 p-1'>
      <InputSimple
        placeholder='email'
        forcedValue={form.email}
        onChange={event => onFieldChange('email', event.target.value)}
      />
      {mode === 'register' && (
        <InputSimple
          placeholder='nom utilisateur'
          forcedValue={form.username}
          onChange={event => onFieldChange('username', event.target.value)}
        />
      )}
      {mode !== 'forgot' && (
        <InputSimple
          placeholder='mot de passe'
          forcedValue={form.password}
          type='password'
          onChange={event => onFieldChange('password', event.target.value)}
        />
      )}
      <MessageLine message={message} />
      <MessageLine
        message={error}
        tone='error'
      />
      <div className='flex justify-end'>
        <BtnSimple
          disabled={loading}
          onClick={onSubmit}
        >
          {loading
            ? 'Traitement...'
            : mode === 'forgot'
              ? 'Envoyer le lien'
              : mode === 'login'
                ? 'Se connecter'
                : 'Créer le compte'}
        </BtnSimple>
      </div>
    </div>
  </section>
);

const AuthLoadingPanel = () => (
  <section className='flex flex-col gap-1 p-0 sm:rounded-lg sm:bg-main sm:bg-opacity-40 sm:p-1 sm:shadow sm:backdrop-blur-sm lg:w-1/2'>
    <div>
      <div className='text-xs font-bold uppercase tracking-wide text-secondary'>
        Connexion Discord
      </div>
      <h2 className='font-condensed text-3xl font-bold uppercase'>
        Connexion en cours
      </h2>
      <p className='text-sm text-contrast text-opacity-65'>
        Le compte Discord a répondu. L'application récupère maintenant ta
        session.
      </p>
    </div>
    <div className='p-0 text-sm text-contrast text-opacity-75 sm:rounded-lg sm:bg-primary sm:bg-opacity-25 sm:p-1'>
      Merci de patienter quelques secondes.
    </div>
  </section>
);

const AccountDeleteModal = ({
  preview,
  choices,
  loading,
  error,
  onChoiceChange,
  onConfirm,
}) => {
  const ownedUniverses = Array.isArray(preview?.ownedUniverses)
    ? preview.ownedUniverses
    : [];
  const forcedDeleteUniverses = ownedUniverses.filter(
    universe => universe.forcedAction === 'delete',
  );
  const choiceUniverses = ownedUniverses.filter(
    universe => universe.requiresChoice,
  );

  return (
    <ModalGlobal
      title='Suppression du compte'
      idToOpen='deleteUserAccount'
      width={760}
      maxWidthClass='max-w-4xl'
    >
      <div className='flex flex-col gap-1'>
        <div className='rounded bg-secondary bg-opacity-20 p-1 text-sm text-contrast'>
          Cette action supprimera définitivement ton compte utilisateur, ton
          profil et ta session.
        </div>

        {forcedDeleteUniverses.length > 0 ? (
          <div className='rounded bg-main bg-opacity-35 p-1'>
            <div className='mb-05 text-xs font-bold uppercase tracking-wide text-secondary'>
              Univers supprimés automatiquement
            </div>
            <p className='mb-05 text-sm text-contrast text-opacity-70'>
              Ces univers n’ont aucun autre admin. Ils seront supprimés avec
              tous leurs dossiers et items.
            </p>
            <div className='flex flex-col gap-05'>
              {forcedDeleteUniverses.map(universe => (
                <div
                  key={universe._id}
                  className='rounded bg-main bg-opacity-40 px-075 py-05 text-sm'
                >
                  {universe.label}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {choiceUniverses.length > 0 ? (
          <div className='rounded bg-main bg-opacity-35 p-1'>
            <div className='mb-05 text-xs font-bold uppercase tracking-wide text-secondary'>
              Univers avec admin
            </div>
            <p className='mb-05 text-sm text-contrast text-opacity-70'>
              Choisis pour chaque univers s’il doit être supprimé ou conservé
              avec transfert à un admin.
            </p>
            <div className='flex flex-col gap-1'>
              {choiceUniverses.map(universe => {
                const choice = choices?.[universe._id] || {};
                return (
                  <div
                    key={universe._id}
                    className='rounded bg-main bg-opacity-40 p-1'
                  >
                    <div className='mb-05 font-condensed text-lg font-bold'>
                      {universe.label}
                    </div>
                    <div className='flex flex-col gap-05'>
                      <label className='flex items-center gap-05 text-sm'>
                        <input
                          type='radio'
                          name={`delete-account-${universe._id}`}
                          checked={choice.action === 'delete'}
                          onChange={() =>
                            onChoiceChange(universe._id, { action: 'delete' })
                          }
                        />
                        Supprimer cet univers et tout son contenu
                      </label>
                      <label className='flex flex-col gap-03 text-sm'>
                        <span className='flex items-center gap-05'>
                          <input
                            type='radio'
                            name={`delete-account-${universe._id}`}
                            checked={choice.action === 'transfer'}
                            onChange={() =>
                              onChoiceChange(universe._id, {
                                action: 'transfer',
                                newOwnerId:
                                  choice.newOwnerId ||
                                  universe.admins?.[0]?._id,
                              })
                            }
                          />
                          Conserver et transférer à un admin
                        </span>
                        {choice.action === 'transfer' ? (
                          <select
                            className='w-full rounded bg-primary p-05 text-contrast'
                            value={choice.newOwnerId || ''}
                            onChange={event =>
                              onChoiceChange(universe._id, {
                                action: 'transfer',
                                newOwnerId: event.target.value,
                              })
                            }
                          >
                            {(universe.admins || []).map(admin => (
                              <option
                                key={admin._id}
                                value={admin._id}
                              >
                                {admin.username || admin.email}
                              </option>
                            ))}
                          </select>
                        ) : null}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {ownedUniverses.length === 0 ? (
          <div className='rounded bg-main bg-opacity-35 p-1 text-sm text-contrast text-opacity-70'>
            Aucun univers possédé par ce compte.
          </div>
        ) : null}

        <MessageLine
          message={error}
          tone='error'
        />
        <div className='flex justify-end gap-1'>
          <BtnSimple
            color='secondary'
            disabled={loading}
            onClick={onConfirm}
          >
            Supprimer mon compte
          </BtnSimple>
        </div>
      </div>
    </ModalGlobal>
  );
};

const ProfileMainCard = ({
  authUser,
  profileForm,
  profileMessage,
  profileError,
  onProfileFieldChange,
  onOpenModal,
  loading,
  onDiscordLink,
  onDiscordUnlink,
  onLogout,
}) => (
  <section className='flex flex-col gap-1 p-0 sm:rounded-lg sm:bg-main sm:bg-opacity-40 sm:p-1 sm:shadow sm:backdrop-blur-sm'>
    <div className='flex flex-col gap-1'>
      <div className='flex flex-col items-center gap-1 sm:flex-row sm:items-center'>
        <ProfileAvatarPreview
          authUser={authUser}
          avatarUrl={profileForm.avatarUrl}
          onClick={event => {
            event.stopPropagation();
            onOpenModal('profileImage');
          }}
        />
        <div className='flex min-w-0 flex-1 flex-col items-center gap-05 sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex min-w-0 flex-col items-center gap-05 sm:items-start'>
            <InputSimple
              forcedValue={profileForm.username}
              onChange={event =>
                onProfileFieldChange('username', event.target.value)
              }
              placeholder='pseudo'
              className='m-0 min-w-0 flex-none bg-transparent p-0 text-center font-condensed text-4xl font-bold uppercase text-contrast text-opacity-100 sm:text-left'
              style={{ margin: 0 }}
            />
            <MessageLine message={profileMessage} />
            <MessageLine
              message={profileError}
              tone='error'
            />
          </div>
          <div className='flex shrink-0 flex-wrap items-center justify-center gap-05 sm:justify-end'>
            <div className='truncate text-sm text-contrast text-opacity-65'>
              {authUser.email}
            </div>
            {authUser.discordId ? (
              <>
                <DiscordStatus authUser={authUser} />
                <BtnSimple
                  sm
                  disabled={loading || authUser.passwordLoginEnabled !== true}
                  className='flex items-center gap-05'
                  onClick={onDiscordUnlink}
                >
                  <FaUnlink />
                  Délier Discord
                </BtnSimple>
              </>
            ) : (
              <>
                <DiscordStatus authUser={authUser} />
                <BtnSimple
                  sm
                  disabled={loading}
                  className='flex items-center gap-05'
                  onClick={onDiscordLink}
                >
                  <FaDiscord />
                  Lier Discord
                </BtnSimple>
              </>
            )}
            <BtnSimple
              sm
              className='flex items-center gap-05'
              onClick={onLogout}
            >
              <FaSignOutAlt />
              Déconnexion
            </BtnSimple>
          </div>
        </div>
      </div>
      <ProfileDescriptionDisplay
        descriptionMarkdown={profileForm.descriptionMarkdown}
        onOpen={event => {
          event.stopPropagation();
          onOpenModal('profileDescription');
        }}
      />
    </div>
    {authUser.discordId && authUser.passwordLoginEnabled !== true ? (
      <div className='rounded bg-main bg-opacity-35 p-05 text-xs text-secondary'>
        Définis un mot de passe avant de délier Discord. Sinon tu ne pourras
        plus te connecter à ce compte.
      </div>
    ) : null}
  </section>
);

const ConnectedPanel = ({
  authUser,
  profileForm,
  profileDraft,
  profileSaving,
  profileImageLoading,
  discordLoading,
  imageSize,
  authMessage,
  authError,
  profileMessage,
  profileError,
  onProfileFieldChange,
  onProfileDraftFieldChange,
  onProfileImageFromDevice,
  onProfileImageFromUrl,
  onProfileImageModalClose,
  onProfileDescriptionModalClose,
  onOpenModal,
  onDiscordLink,
  onDiscordUnlink,
  onAccountDeleteConfirm,
  accountDeletePreview,
  accountDeleteChoices,
  accountDeleteError,
  onAccountDeleteChoiceChange,
  onLogout,
}) => (
  <div className='flex flex-col gap-2'>
    <ProfileMainCard
      authUser={authUser}
      profileForm={profileForm}
      profileMessage={profileMessage}
      profileError={profileError}
      onProfileFieldChange={onProfileFieldChange}
      onOpenModal={onOpenModal}
      loading={discordLoading}
      onDiscordLink={onDiscordLink}
      onDiscordUnlink={onDiscordUnlink}
      onLogout={onLogout}
    />
    <MessageLine message={authMessage} />
    <MessageLine
      message={authError}
      tone='error'
    />
    <AccountDeleteModal
      preview={accountDeletePreview}
      choices={accountDeleteChoices}
      loading={discordLoading}
      error={accountDeleteError}
      onChoiceChange={onAccountDeleteChoiceChange}
      onConfirm={onAccountDeleteConfirm}
    />
    <ProfileImageModal
      authUser={authUser}
      profileDraft={profileDraft}
      imageSize={imageSize}
      profileImageLoading={profileImageLoading}
      profileSaving={profileSaving}
      profileMessage={profileMessage}
      profileError={profileError}
      onProfileDraftFieldChange={onProfileDraftFieldChange}
      onProfileImageFromDevice={onProfileImageFromDevice}
      onProfileImageFromUrl={onProfileImageFromUrl}
      onClose={onProfileImageModalClose}
    />
    <ProfileDescriptionModal
      profileDraft={profileDraft}
      profileSaving={profileSaving}
      profileMessage={profileMessage}
      profileError={profileError}
      onProfileDraftFieldChange={onProfileDraftFieldChange}
      onClose={onProfileDescriptionModalClose}
    />
  </div>
);

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useSelector(state => state.global.theme);
  const isMobile = useSelector(state => state.global.isMobile);
  const navigation = useSelector(state => state.global.navigation);
  const history = useSelector(state => state.global.history || []);
  const items = useSelector(state => state.items || {});
  const [authUser, setAuthUser] = useState(getStoredAuthUser());
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    email: '',
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [roleUniverses, setRoleUniverses] = useState([]);
  const [roleCampaigns, setRoleCampaigns] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({
    username: '',
    avatarUrl: '',
    descriptionMarkdown: '',
  });
  const [profileDraft, setProfileDraft] = useState({
    avatarUrl: '',
    descriptionMarkdown: '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileImageLoading, setProfileImageLoading] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 100, height: 100 });
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [authActionLoading, setAuthActionLoading] = useState(false);
  const [accountDeletePreview, setAccountDeletePreview] = useState(null);
  const [accountDeleteChoices, setAccountDeleteChoices] = useState({});
  const [accountDeleteError, setAccountDeleteError] = useState('');
  const [recentItemIds, setRecentItemIds] = useState(
    getRecentItemIdsFromStorage,
  );
  const [authBootstrapLoading, setAuthBootstrapLoading] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('auth_token'));
  });

  const recentItems = useMemo(
    () => recentItemIds.map(id => items[id]).filter(Boolean),
    [recentItemIds, items],
  );

  useEffect(() => {
    if (!Array.isArray(history) || history.length === 0) return;
    setRecentItemIds(prev =>
      Array.from(new Set([...history, ...prev])).slice(0, 100),
    );
  }, [history]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('auth_token');
    const discordStatus = params.get('discord');
    if (tokenFromUrl) {
      window.localStorage.setItem('auth_token', tokenFromUrl);
      setAuthBootstrapLoading(true);
      setMessage('Connexion Discord en cours...');
      params.delete('auth_token');
    }
    if (discordStatus) {
      const statusMessage =
        discordStatusLabels[discordStatus] || discordStatusLabels.error;
      if (discordStatus === 'connected' || discordStatus === 'linked') {
        setMessage(statusMessage);
      } else {
        setError(statusMessage);
      }
      params.delete('discord');
    }
    if (tokenFromUrl || discordStatus) {
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
      window.history.replaceState(null, '', nextUrl);
    }
  }, []);

  useEffect(() => {
    const token = window.localStorage.getItem('auth_token');
    if (!token) {
      setAuthBootstrapLoading(false);
      return;
    }
    let cancelled = false;
    if (!window.localStorage.getItem('auth_user')) {
      setAuthBootstrapLoading(true);
    }

    getMe(token)
      .then(data => {
        if (!cancelled && data?.user) {
          setAuthUser(data.user);
          syncAuthSession(data.user);
        }
      })
      .catch(errorResponse => {
        const status = errorResponse?.response?.status;
        if (status === 401 || status === 403) {
          window.localStorage.removeItem('auth_token');
          window.localStorage.removeItem('auth_user');
          if (!cancelled) {
            setAuthUser(null);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAuthBootstrapLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authUser?._id) {
      setRoleUniverses([]);
      setRoleCampaigns([]);
      return;
    }

    let cancelled = false;
    setRoleLoading(true);
    loadUserRoles(authUser._id.toString())
      .then(data => {
        if (cancelled) return;
        setRoleUniverses(data.roleUniverses);
        setRoleCampaigns(data.roleCampaigns);
      })
      .catch(() => {
        if (cancelled) return;
        setRoleUniverses([]);
        setRoleCampaigns([]);
      })
      .finally(() => {
        if (!cancelled) {
          setRoleLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authUser?._id]);

  useEffect(() => {
    setProfileForm({
      username: authUser?.username || '',
      avatarUrl: authUser?.avatarUrl || '',
      descriptionMarkdown: authUser?.descriptionMarkdown || '',
    });
    setProfileDraft({
      avatarUrl: authUser?.avatarUrl || '',
      descriptionMarkdown: authUser?.descriptionMarkdown || '',
    });
  }, [
    authUser?._id,
    authUser?.username,
    authUser?.avatarUrl,
    authUser?.descriptionMarkdown,
  ]);

  useEffect(() => {
    if (!profileDraft.avatarUrl) {
      setImageSize({ width: 100, height: 100 });
      return undefined;
    }

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setImageSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setImageSize({ width: 100, height: 100 });
      }
    };
    img.src = profileDraft.avatarUrl;

    return () => {
      cancelled = true;
    };
  }, [profileDraft.avatarUrl]);

  const handleFieldChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const resetFeedback = () => {
    setMessage('');
    setError('');
  };

  const handleSubmit = async () => {
    resetFeedback();
    setLoading(true);
    try {
      if (mode === 'forgot') {
        await requestPasswordReset({ email: form.email.trim() });
        setMessage(
          'Si cet email existe, un lien de réinitialisation a été envoyé.',
        );
        return;
      }

      if (mode === 'login') {
        const data = await login({
          email: form.email.trim(),
          password: form.password,
        });
        if (data?.token) {
          window.localStorage.setItem('auth_token', data.token);
        }
        if (data?.user) {
          setAuthUser(data.user);
          await syncAuthSession(data.user);
        }
        setMessage('Connexion réussie.');
        setForm(prev => ({ ...prev, password: '' }));
        return;
      }

      await register({
        email: form.email.trim(),
        username: form.username.trim(),
        password: form.password,
      });
      setMode('login');
      setMessage('Compte créé, connecte-toi.');
      setForm(prev => ({ ...prev, password: '' }));
    } catch (e) {
      if (mode === 'forgot') {
        setError('envoi impossible');
      } else {
        setError(
          mode === 'login' ? 'connexion impossible' : 'inscription impossible',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    window.localStorage.removeItem('auth_token');
    window.localStorage.removeItem('auth_user');
    setAuthUser(null);
    setMessage('Déconnecté.');
    setError('');
    setProfileMessage('');
    setProfileError('');
  };

  const handleModeChange = nextMode => {
    setMode(nextMode);
    resetFeedback();
  };

  const handleProfileFieldChange = (key, value) => {
    setProfileForm(prev => ({ ...prev, [key]: value }));
    setProfileMessage('');
    setProfileError('');
  };

  const handleProfileDraftFieldChange = (key, value) => {
    setProfileDraft(prev => ({ ...prev, [key]: value }));
    setProfileMessage('');
    setProfileError('');
  };

  const setProfileAvatarUrl = avatarUrl => {
    setProfileDraft(prev => ({ ...prev, avatarUrl }));
    setProfileMessage('');
    setProfileError('');
  };

  const handleProfileImageFromDevice = async event => {
    if (!event?.target?.files?.[0]) return;
    setProfileImageLoading(true);
    setProfileError('');
    try {
      const res = await uploadPic(event);
      if (res?.data?.url) {
        setProfileAvatarUrl(res.data.url);
      }
    } catch (e) {
      setProfileError("import de l'image impossible");
    } finally {
      setProfileImageLoading(false);
      event.target.value = '';
    }
  };

  const handleProfileImageFromUrl = async value => {
    const trimmedValue = typeof value === 'string' ? value.trim() : '';
    if (!trimmedValue) {
      setProfileAvatarUrl('');
      return;
    }

    setProfileImageLoading(true);
    setProfileError('');
    try {
      const res = await uploadPicFromUrl(trimmedValue);
      if (res?.data?.url) {
        setProfileAvatarUrl(res.data.url);
      }
    } catch (e) {
      setProfileError("import de l'image impossible");
    } finally {
      setProfileImageLoading(false);
    }
  };

  useEffect(() => {
    if (!authUser?._id) {
      return undefined;
    }

    const nextUsername = profileForm.username.trim();
    const currentUsername = authUser.username || '';

    if (nextUsername === currentUsername) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      const payload = {
        username: nextUsername,
        avatarUrl: authUser.avatarUrl || '',
        descriptionMarkdown: authUser.descriptionMarkdown || '',
      };

      setProfileSaving(true);
      setProfileMessage('');
      setProfileError('');
      updateProfile(payload)
        .then(async data => {
          const nextUser = data?.user || { ...authUser, ...payload };
          setAuthUser(nextUser);
          await syncAuthSession(nextUser);
          setProfileMessage('Profil mis à jour.');
        })
        .catch(errorResponse => {
          const errorCode = errorResponse?.response?.data?.error;
          setProfileError(
            profileSaveErrorLabels[errorCode] ||
              'mise à jour du profil impossible',
          );
        })
        .finally(() => {
          setProfileSaving(false);
        });
    }, 700);

    return () => window.clearTimeout(timeout);
  }, [
    authUser,
    authUser?._id,
    authUser?.username,
    authUser?.avatarUrl,
    authUser?.descriptionMarkdown,
    profileForm.username,
  ]);

  const saveProfileDraft = patch => {
    if (!authUser?._id) {
      return;
    }

    const payload = {
      username: authUser.username || '',
      avatarUrl: patch.avatarUrl ?? authUser.avatarUrl ?? '',
      descriptionMarkdown:
        patch.descriptionMarkdown ?? authUser.descriptionMarkdown ?? '',
    };

    if (
      payload.avatarUrl === (authUser.avatarUrl || '') &&
      payload.descriptionMarkdown === (authUser.descriptionMarkdown || '')
    ) {
      return;
    }

    setProfileSaving(true);
    setProfileMessage('');
    setProfileError('');
    updateProfile(payload)
      .then(async data => {
        const nextUser = data?.user || { ...authUser, ...payload };
        setAuthUser(nextUser);
        await syncAuthSession(nextUser);
        setProfileMessage('Profil mis à jour.');
      })
      .catch(errorResponse => {
        const errorCode = errorResponse?.response?.data?.error;
        setProfileError(
          profileSaveErrorLabels[errorCode] ||
            'mise à jour du profil impossible',
        );
      })
      .finally(() => {
        setProfileSaving(false);
      });
  };

  const handleProfileImageModalClose = () => {
    saveProfileDraft({ avatarUrl: profileDraft.avatarUrl.trim() });
  };

  const handleProfileDescriptionModalClose = () => {
    saveProfileDraft({
      descriptionMarkdown: profileDraft.descriptionMarkdown,
    });
  };

  const startDiscordFlow = async mode => {
    setAuthActionLoading(true);
    setError('');
    setMessage('');
    try {
      const data = await getDiscordAuthUrl({
        mode,
        returnTo: '/profile',
      });
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      setError('connexion Discord impossible');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        setError("Discord n'est pas encore configuré côté serveur.");
      } else {
        setError('connexion Discord impossible');
      }
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleDiscordUnlink = async () => {
    if (authUser?.passwordLoginEnabled !== true) {
      setError(discordErrorLabels.password_required_before_unlink);
      setMessage('');
      return;
    }
    setAuthActionLoading(true);
    setError('');
    setMessage('');
    try {
      const data = await unlinkDiscordAccount();
      if (data?.user) {
        setAuthUser(data.user);
        await syncAuthSession(data.user);
      }
      setMessage('Compte Discord délié.');
    } catch (e) {
      const errorCode = e?.response?.data?.error;
      setError(discordErrorLabels[errorCode] || 'impossible de délier Discord');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleAccountDeleteRequest = async () => {
    setError('');
    setMessage('');
    setAccountDeleteError('');
    setAuthActionLoading(true);
    try {
      const preview = await getAccountDeletionPreview();
      const ownedUniverses = Array.isArray(preview?.ownedUniverses)
        ? preview.ownedUniverses
        : [];
      const initialChoices = {};
      ownedUniverses.forEach(universe => {
        if (universe.requiresChoice && universe.admins?.[0]?._id) {
          initialChoices[universe._id] = {
            action: 'transfer',
            newOwnerId: universe.admins[0]._id,
          };
        }
      });
      setAccountDeletePreview({ ownedUniverses });
      setAccountDeleteChoices(initialChoices);
      dispatch(setOpenModales({ id: 'deleteUserAccount', open: true }));
    } catch (e) {
      setError('préparation de la suppression impossible');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleAccountDeleteChoiceChange = (universeId, choice) => {
    setAccountDeleteError('');
    setAccountDeleteChoices(prev => ({
      ...prev,
      [universeId]: choice,
    }));
  };

  const handleAccountDeleteConfirm = async () => {
    const choiceUniverses = (accountDeletePreview?.ownedUniverses || []).filter(
      universe => universe.requiresChoice,
    );
    const missingChoice = choiceUniverses.some(universe => {
      const choice = accountDeleteChoices[universe._id];
      if (!choice?.action) return true;
      return choice.action === 'transfer' && !choice.newOwnerId;
    });
    if (missingChoice) {
      setAccountDeleteError(accountDeleteErrorLabels.missing_universe_choices);
      return;
    }

    setAuthActionLoading(true);
    setError('');
    setMessage('');
    setAccountDeleteError('');
    try {
      await deleteUserAccount({ universes: accountDeleteChoices });
      window.localStorage.removeItem('auth_token');
      window.localStorage.removeItem('auth_user');
      setAuthUser(null);
      setRoleUniverses([]);
      setRoleCampaigns([]);
      setProfileMessage('');
      setProfileError('');
      setMessage('Compte supprimé.');
      navigate('/');
    } catch (e) {
      const errorCode = e?.response?.data?.error;
      setAccountDeleteError(
        accountDeleteErrorLabels[errorCode] ||
          'suppression du compte impossible',
      );
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleOpenModal = modalId => {
    setProfileMessage('');
    setProfileError('');
    dispatch(setOpenModales({ id: modalId, open: true }));
  };

  return (
    <div className='relative h-screen overflow-hidden bg-primary-d text-contrast'>
      <div className='pointer-events-none fixed inset-0 z-0 flex items-center justify-center opacity-10'>
        {logoSVG(theme, { maxHeight: '100%', padding: 100 })}
      </div>
      <div className='relative z-10 flex h-full w-full'>
        <div className='min-w-0 flex-1 overflow-y-auto p-2'>
          <div className='flex w-full flex-col gap-2'>
            <header className='flex flex-col gap-1 lg:flex-row lg:items-end lg:justify-between'>
              <div>
                <div className='text-xs font-bold uppercase tracking-wide text-secondary'>
                  Compte
                </div>
                <h1 className='font-condensed text-4xl font-bold uppercase'>
                  Profil
                </h1>
                <p className='w-full text-sm text-contrast text-opacity-70'>
                  Gère ton compte et consulte les univers ou dossiers auxquels
                  tu as accès.
                </p>
              </div>
            </header>

            <div className='flex flex-col gap-2'>
              {authUser ? (
                <ConnectedPanel
                  authUser={authUser}
                  profileForm={profileForm}
                  profileDraft={profileDraft}
                  profileSaving={profileSaving}
                  profileImageLoading={profileImageLoading}
                  discordLoading={authActionLoading}
                  imageSize={imageSize}
                  authMessage={message}
                  authError={error}
                  profileMessage={profileMessage}
                  profileError={profileError}
                  onProfileFieldChange={handleProfileFieldChange}
                  onProfileDraftFieldChange={handleProfileDraftFieldChange}
                  onProfileImageFromDevice={handleProfileImageFromDevice}
                  onProfileImageFromUrl={handleProfileImageFromUrl}
                  onProfileImageModalClose={handleProfileImageModalClose}
                  onProfileDescriptionModalClose={
                    handleProfileDescriptionModalClose
                  }
                  onOpenModal={handleOpenModal}
                  onDiscordLink={() => startDiscordFlow('link')}
                  onDiscordUnlink={handleDiscordUnlink}
                  onAccountDeleteConfirm={handleAccountDeleteConfirm}
                  accountDeletePreview={accountDeletePreview}
                  accountDeleteChoices={accountDeleteChoices}
                  accountDeleteError={accountDeleteError}
                  onAccountDeleteChoiceChange={handleAccountDeleteChoiceChange}
                  onLogout={handleLogout}
                />
              ) : (
                <div className='flex flex-col gap-2 lg:flex-row'>
                  {authBootstrapLoading ? (
                    <AuthLoadingPanel />
                  ) : (
                    <AuthPanel
                      mode={mode}
                      form={form}
                      loading={loading || authActionLoading}
                      message={message}
                      error={error}
                      onModeChange={handleModeChange}
                      onFieldChange={handleFieldChange}
                      onSubmit={handleSubmit}
                      onDiscordLogin={() => startDiscordFlow('login')}
                    />
                  )}
                  <section className='flex flex-1 flex-col gap-1 p-0 sm:rounded-lg sm:bg-main sm:bg-opacity-40 sm:p-1 sm:shadow sm:backdrop-blur-sm'>
                    <div>
                      <div className='text-xs font-bold uppercase tracking-wide text-secondary'>
                        Permissions
                      </div>
                      <h2 className='font-condensed text-3xl font-bold uppercase'>
                        Accès
                      </h2>
                      <p className='text-sm text-contrast text-opacity-65'>
                        Connecte-toi pour afficher les rôles liés à ton compte.
                      </p>
                    </div>
                    <div className='p-0 text-sm text-contrast text-opacity-65 sm:rounded-lg sm:bg-main sm:bg-opacity-30 sm:p-1'>
                      Connecte-toi pour afficher tes accès.
                    </div>
                  </section>
                </div>
              )}

              {authUser ? (
                <section className='flex flex-col gap-1 p-0 sm:rounded-lg sm:bg-main sm:bg-opacity-40 sm:p-1 sm:shadow sm:backdrop-blur-sm'>
                  <div>
                    <div className='text-xs font-bold uppercase tracking-wide text-secondary'>
                      Permissions
                    </div>
                    <h2 className='font-condensed text-3xl font-bold uppercase'>
                      Permissions
                    </h2>
                    <p className='text-sm text-contrast text-opacity-65'>
                      Les univers que tu possèdes ou administres sont séparés
                      des accès reçus directement sur des dossiers.
                    </p>
                  </div>
                  <div className='flex flex-col gap-1 xl:flex-row'>
                    <RoleSection
                      title='Univers administrés / créés'
                      entries={roleUniverses}
                      type='universe'
                      loading={roleLoading}
                      onOpen={itemId => navigate(`/item/${itemId}`)}
                    />
                    <RoleSection
                      title='Accès reçus'
                      entries={roleCampaigns}
                      type='campaign'
                      loading={roleLoading}
                      onOpen={itemId => navigate(`/item/${itemId}`)}
                    />
                  </div>
                </section>
              ) : null}

              {authUser ? (
                <section className='flex flex-col gap-1 p-0 sm:rounded-lg sm:bg-main sm:bg-opacity-40 sm:p-1 sm:shadow sm:backdrop-blur-sm'>
                  <div>
                    <div className='text-xs font-bold uppercase tracking-wide text-secondary'>
                      Zone sensible
                    </div>
                    <h2 className='font-condensed text-3xl font-bold uppercase'>
                      Suppression du compte
                    </h2>
                    <p className='text-sm text-contrast text-opacity-65'>
                      Cette action peut supprimer les univers que tu possèdes et
                      leur contenu.
                    </p>
                    <p className='mt-05 text-sm font-bold text-secondary'>
                      Les univers sans autre admin seront supprimés
                      définitivement avec tous leurs dossiers et items.
                    </p>
                  </div>
                  <div className='flex justify-end'>
                    <BtnSimple
                      color='secondary-d'
                      disabled={authActionLoading}
                      className='flex items-center gap-05'
                      onClick={handleAccountDeleteRequest}
                    >
                      <FaTrashAlt />
                      Supprimer mon compte
                    </BtnSimple>
                  </div>
                </section>
              ) : null}
            </div>
          </div>
        </div>
        {!isMobile && (
          <ProfileHistorySidebar
            navigationSize={navigation?.size}
            recentItems={recentItems}
            onOpenItem={itemId => navigate(`/item/${itemId}`)}
            onOpenPasswords={() =>
              dispatch(setOpenModales({ id: 'managePassword', open: true }))
            }
          />
        )}
      </div>
    </div>
  );
};

export default Profile;
